import { forwardRef, type InputHTMLAttributes, useId } from 'react';

import { cn } from '../../utils';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  description?: string;
  error?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, description, error, className, id, disabled, ...props }, ref) => {
    const reactId = useId();
    const inputId = id || `checkbox-${reactId}`;
    const hasError = Boolean(error);

    return (
      <div className={cn('relative flex', className)}>
        <div className="flex h-5 items-center">
          <input
            ref={ref}
            id={inputId}
            type="checkbox"
            className={cn(
              'h-4 w-4 rounded border transition-colors duration-200',
              'focus:outline-none focus:ring-2 focus:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
              hasError
                ? 'border-tkf-line-error accent-error-500 focus:ring-tkf-line-error'
                : 'border-tkf-line-medium accent-primary-500 focus:ring-primary-500'
            )}
            disabled={disabled}
            aria-invalid={hasError}
            aria-describedby={
              hasError ? `${inputId}-error` : description ? `${inputId}-description` : undefined
            }
            {...props}
          />
        </div>
        {(label || description) && (
          <div className="ml-3">
            {label && (
              <label
                htmlFor={inputId}
                className={cn(
                  'text-sm font-medium',
                  disabled ? 'text-tkf-fg-disabled' : 'text-tkf-fg'
                )}
              >
                {label}
              </label>
            )}
            {description && (
              <p id={`${inputId}-description`} className="text-sm text-tkf-fg-muted">
                {description}
              </p>
            )}
            {error && (
              <p id={`${inputId}-error`} className="text-sm text-tkf-fg-error">
                {error}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
